import { api } from '@/lib/api';
import type { ProtocolByRegion } from '@valiosascrm/shared';

export type { LaserRegionProtocol, ProtocolByRegion } from '@valiosascrm/shared';

export interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number;
  price: number;
  isActive: boolean;
  requiresAnamnesis: boolean;
  protocolByRegion?: ProtocolByRegion | null;
  products?: { id: string; name: string; quantity: number }[];
  createdAt: string;
  updatedAt: string;
}

export interface ServiceListResult {
  data: Service[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ServiceQueryParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  /**
   * Story 51.4 — informar a paciente devolve o catálogo MAIS os procedimentos sob medida
   * DELA. Sem este parâmetro, sob medida nunca aparece: é o que mantém o catálogo limpo
   * em Configurações e nos demais seletores.
   */
  bespokeForPatientId?: string;
}

export interface CreateServiceData {
  /** Story 51.4 — quando presente, o serviço nasce sob medida para esta paciente. */
  bespokeForPatientId?: string;
  name: string;
  description?: string;
  duration: number;
  price: number;
  requiresAnamnesis?: boolean;
  protocolByRegion?: ProtocolByRegion | null;
  /** Story 65.17 — pedido de criação: repetir com o mesmo pedido devolve o serviço já criado. */
  requestId?: string;
}

export interface UpdateServiceData extends Partial<CreateServiceData> {}

export async function getServices(params: ServiceQueryParams = {}): Promise<ServiceListResult> {
  const { data } = await api.get<ServiceListResult>('/services', { params });
  return data;
}

export async function getAllServices(
  params: Omit<ServiceQueryParams, 'page' | 'limit'> = {},
): Promise<Service[]> {
  const limit = 100;
  let page = 1;
  const all: Service[] = [];
  while (page <= 50) {
    const result = await getServices({ ...params, page, limit });
    all.push(...result.data);
    if (page >= result.totalPages || result.data.length === 0) break;
    page += 1;
  }
  return all;
}

export async function getService(id: string): Promise<Service> {
  const { data } = await api.get<Service>(`/services/${id}`);
  return data;
}

export async function createService(serviceData: CreateServiceData): Promise<Service> {
  const { data } = await api.post<Service>('/services', serviceData);
  return data;
}

export async function updateService(id: string, serviceData: UpdateServiceData): Promise<Service> {
  const { data } = await api.patch<Service>(`/services/${id}`, serviceData);
  return data;
}

export async function deactivateService(id: string): Promise<{ message: string }> {
  const { data } = await api.delete<{ message: string }>(`/services/${id}`);
  return data;
}

export async function activateService(id: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/services/${id}/activate`);
  return data;
}

export async function deleteServicePermanently(id: string): Promise<{ deleted: true }> {
  const { data } = await api.delete<{ deleted: true }>(`/services/${id}/permanent`);
  return data;
}
